import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, RouteComponentProps } from 'react-router-dom'
import { ApplicationState } from '../store'
import { actionCreators as countriesActions } from '../store/Countries'
import { actionCreators as citiesActions } from '../store/Cities'
import {
    ContainerWithPadding,
    DataTable,
    DataTableColumnModel,
    DataTableModel,
    DataTableModelCustomAction,
    Select,
    SelectOptionModel
} from 'traveldesk-ui'
import HotelsApiService from '../services/HotelsApiService'
import HotelImportModel from '../models/HotelImportModel'
import ICityOptionModel from '../models/CityWithRegionsOptionModel'
import { urls } from '../urls'

const normalize = (value?: string) => (value ?? '').trim().toLowerCase()
const hasValue = (value?: string) => (value ?? '').trim().length > 0
const toNumber = (value: string) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

const recalculateRows = (rows: HotelImportModel[]) => {
    const updated = rows.map(x => HotelImportModel.Create(x))
    updated.forEach(row => {
        const errors: string[] = []
        if (!row.isDeleted) {
            if (!hasValue(row.name)) {
                errors.push('Name is required')
            }
            if (row.countryId <= 0) {
                errors.push('Country is required')
            }
            if (!row.cityId && !row.addCityOnImport) {
                errors.push('City is required')
            }
            if (row.addCityOnImport && !hasValue(row.cityName)) {
                errors.push('City name is required for add on import')
            }
            if (!row.regionId && !row.addRegionOnImport) {
                errors.push('Region is required')
            }
            if (row.addRegionOnImport && !hasValue(row.regionName)) {
                errors.push('Region name is required for add on import')
            }
            if (row.saveError) {
                errors.push(row.saveError)
            }
        }
        row.errors = errors        
    })
    

    updated.forEach(row => {
        row.hasError = row.errors.length > 0
        row.isLocked = row.matchedByName && !row.isDuplicate && !row.hasError && !row.isDeleted
    })
    return updated
}

interface Props extends RouteComponentProps { }

const HotelsImportPage = (props: Props) => {
    const dispatch = useDispatch()
    const countries = useSelector((state: ApplicationState) => state.countries)
    const cities = useSelector((state: ApplicationState) => state.cities)
    const localization = useSelector((state: ApplicationState) => state.localization.locale)

    const [rows, setRows] = React.useState<HotelImportModel[]>([])
    const [selectedFile, setSelectedFile] = React.useState<File | undefined>(undefined)
    const [isPreviewLoading, setPreviewLoading] = React.useState(false)
    const [isImportLoading, setImportLoading] = React.useState(false)
    const [lastMessage, setLastMessage] = React.useState('')
    const pendingCitiesCountriesRef = React.useRef<Set<number>>(new Set<number>())

    React.useEffect(() => {
        dispatch(countriesActions.requestCountriesOptions())
    }, [])

    const ensureCitiesLoaded = (countryId: number) => {
        if (countryId <= 0) {
            return
        }
        if (cities.citiesInCountryWithRegionsOptions.get(countryId)) {
            return
        }
        if (pendingCitiesCountriesRef.current.has(countryId)) {
            return
        }
        pendingCitiesCountriesRef.current.add(countryId)
        Promise.resolve(dispatch(citiesActions.requestCitiesOptionsInCountryWithRegions(countryId)) as any)
            .finally(() => pendingCitiesCountriesRef.current.delete(countryId))
    }

    const updateRows = (update: (draft: HotelImportModel[]) => void) => {
        setRows(prev => {
            const draft = prev.map(x => HotelImportModel.Create(x))
            update(draft)
            return recalculateRows(draft)
        })
    }

    const getCitiesOptions = (countryId: number) => cities.citiesInCountryWithRegionsOptions.get(countryId) ?? []
    const getRegionsOptions = (row: HotelImportModel) => {
        if (!row.countryId || !row.cityId) {
            return [] as SelectOptionModel[]
        }
        const city = getCitiesOptions(row.countryId).find(x => x.value === row.cityId)
        return city?.regions ?? []
    }

    const onApplyCountry = (row: HotelImportModel, option?: SelectOptionModel) => {
        if (!option || row.isLocked) {
            return
        }
        const countryId = option.value as number
        ensureCitiesLoaded(countryId)
        const countryToken = normalize(row.countryName)
        updateRows(draft => {
            draft.forEach(item => {
                if (item.isDeleted || item.isLocked) {
                    return
                }
                const shouldApply =
                    item.rowNumber === row.rowNumber ||
                    (countryToken.length > 0 && normalize(item.countryName) === countryToken && item.countryId <= 0)
                if (!shouldApply) {
                    return
                }
                if (item.countryId !== countryId) {
                    item.cityId = undefined
                    item.regionId = undefined
                    item.addCityOnImport = false
                    item.addRegionOnImport = false
                }
                item.countryId = countryId
                item.canAddCity = true
            })
        })
    }

    const onApplyCity = (row: HotelImportModel, option?: SelectOptionModel) => {
        if (!option || row.isLocked || !row.countryId) {
            return
        }
        const cityId = option.value as number
        const cityToken = normalize(row.cityName)
        updateRows(draft => {
            draft.forEach(item => {
                if (item.isDeleted || item.isLocked || item.countryId !== row.countryId) {
                    return
                }
                const shouldApply =
                    item.rowNumber === row.rowNumber ||
                    (cityToken.length > 0 && normalize(item.cityName) === cityToken && !item.cityId)
                if (!shouldApply) {
                    return
                }
                if (item.cityId !== cityId) {
                    item.regionId = undefined
                    item.addRegionOnImport = false
                }
                item.cityId = cityId
                item.addCityOnImport = false
                item.canAddRegion = true
            })
        })
    }

    const onApplyRegion = (row: HotelImportModel, option?: SelectOptionModel) => {
        if (!option || row.isLocked || !row.cityId) {
            return
        }
        const regionId = option.value as number
        const regionToken = normalize(row.regionName)
        updateRows(draft => {
            draft.forEach(item => {
                if (item.isDeleted || item.isLocked || item.cityId !== row.cityId) {
                    return
                }
                const shouldApply =
                    item.rowNumber === row.rowNumber ||
                    (regionToken.length > 0 && normalize(item.regionName) === regionToken && !item.regionId)
                if (!shouldApply) {
                    return
                }
                item.regionId = regionId
                item.addRegionOnImport = false
            })
        })
    }

    const onSetAddCity = (row: HotelImportModel, value: boolean) => {
        if (row.isLocked) {
            return
        }
        updateRows(draft => {
            const target = draft.find(x => x.rowNumber === row.rowNumber)
            if (!target) {
                return
            }
            target.addCityOnImport = value
            if (value) {
                target.cityId = undefined
            }
        })
    }

    const onSetAddRegion = (row: HotelImportModel, value: boolean) => {
        if (row.isLocked) {
            return
        }
        updateRows(draft => {
            const target = draft.find(x => x.rowNumber === row.rowNumber)
            if (!target) {
                return
            }
            target.addRegionOnImport = value
            if (value) {
                target.regionId = undefined
            }
        })
    }

    const onDeleteDuplicate = (row: HotelImportModel) => {
        updateRows(draft => {
            const target = draft.find(x => x.rowNumber === row.rowNumber)
            if (target) {
                target.isDeleted = true
            }
        })
    }

    const loadPreview = async () => {
        if (!selectedFile) {
            alert('Please select an Excel file')
            return
        }
        setPreviewLoading(true)
        setLastMessage('')
        const response = await HotelsApiService.importPreview(selectedFile)
        setPreviewLoading(false)
        if (!response.IsOk || !response.Content) {
            alert(response.ErrorMessage || response.ErrorTitle || 'Could not load preview')
            return
        }
        const hotels = recalculateRows(response.Content.hotels)
        setRows(hotels)
        Array.from(new Set(hotels.filter(x => x.countryId > 0).map(x => x.countryId))).forEach(ensureCitiesLoaded)
        setLastMessage(
            `Preview loaded: ${response.Content.countOk} ready, ${response.Content.countErrors} with errors, ${response.Content.countDuplicates} duplicates`,
        )
    }

    const onImport = async () => {
        if (isImportLoading) {
            return
        }
        setImportLoading(true)
        setLastMessage('')
        const response = await HotelsApiService.importHotels(rows, dispatch)
        setImportLoading(false)
        if (!response.IsOk || !response.Content) {
            alert(response.ErrorMessage || response.ErrorTitle || 'Import failed')
            return
        }
        setRows(recalculateRows(response.Content.hotels))
        setLastMessage(`Imported: ${response.Content.countSaved}, failed: ${response.Content.countFailed}, duplicates: ${response.Content.countDuplicates}`)
    }

    const activeRows = rows.filter(x => !x.isDeleted)
    const canImport = activeRows.length > 0 && activeRows.every(x => !x.hasError)

    const dataTableModel = React.useMemo(() => {
        const columns = [
            new DataTableColumnModel({
                fieldName: 'rowNumber',
                displayName: '#',
                width: 40,
            }),
            new DataTableColumnModel({
                fieldName: 'name',
                displayName: 'Name',
                width: 220,
            }),
            new DataTableColumnModel({
                fieldName: 'countryId',
                displayName: 'Country',
                width: 220,
                customRenderer: (row: HotelImportModel) => {
                    if (row.isDeleted) {
                        return <span>-</span>
                    }
                    if (row.isLocked || row.countryId > 0) {
                        const label = countries.countriesOptions.find(x => x.value === row.countryId)?.label ?? row.countryName
                        return <span style={{ color: row.countryId <= 0 ? '#b00020' : undefined }}>{label || row.countryName}</span>
                    }
                    return <Select options={countries.countriesOptions} value={row.countryId || undefined} onChange={(option) => onApplyCountry(row, option)} />
                },
            }),
            new DataTableColumnModel({
                fieldName: 'cityId',
                displayName: 'City',
                width: 260,
                customRenderer: (row: HotelImportModel) => {
                    if (row.isDeleted) {
                        return <span>-</span>
                    }
                    const options = getCitiesOptions(row.countryId)
                    const label = options.find(x => x.value === row.cityId)?.label ?? row.cityName
                    return <div style={{ display: 'grid', gap: 4 }}>
                        {row.isLocked || row.cityId
                            ? <span style={{ color: (!row.cityId && !row.addCityOnImport) ? '#b00020' : undefined }}>{label}</span>
                            : <Select options={options} value={row.cityId} onChange={(option) => onApplyCity(row, option)} />}
                        {(row.canAddCity || row.addCityOnImport) && !row.isLocked
                            ? <label style={{ fontSize: 12 }}>
                                <input type="checkbox" checked={row.addCityOnImport} onChange={e => onSetAddCity(row, e.target.checked)} />
                                {' '}Add on import
                            </label>
                            : undefined}
                    </div>
                }
            }),
            new DataTableColumnModel({
                fieldName: 'regionId',
                displayName: 'Region',
                width: 260,
                customRenderer: (row: HotelImportModel) => {
                    if (row.isDeleted) {
                        return <span>-</span>
                    }
                    const options = getRegionsOptions(row)
                    const label = options.find(x => x.value === row.regionId)?.label ?? row.regionName
                    return <div style={{ display: 'grid', gap: 4 }}>
                        {row.isLocked || row.regionId
                            ? <span style={{ color: (!row.regionId && !row.addRegionOnImport) ? '#b00020' : undefined }}>{label}</span>
                            : <Select options={options} value={row.regionId} onChange={(option) => onApplyRegion(row, option)} />}
                        {(row.canAddRegion || row.addRegionOnImport) && !row.isLocked
                            ? <label style={{ fontSize: 12 }}>
                                <input type="checkbox" checked={row.addRegionOnImport} onChange={e => onSetAddRegion(row, e.target.checked)} />
                                {' '}Add on import
                            </label>
                            : undefined}
                    </div>
                }
            }),
            new DataTableColumnModel({
                fieldName: 'address',
                displayName: 'Address',
                width: 180,
            }),
            new DataTableColumnModel({
                fieldName: 'phone',
                displayName: 'Phone',
                width: 120,
            }),
            new DataTableColumnModel({
                fieldName: 'email',
                displayName: 'Email',
                width: 180,
            }),
            new DataTableColumnModel({
                fieldName: 'latitude',
                displayName: 'Latitude',
                width: 90,
                customRenderer: (row: HotelImportModel) => <input
                    style={{width:"100%"}}
                    value={typeof row.latitude === 'number' ? row.latitude : ''}
                    onChange={e => {
                        if (row.isLocked || row.isDeleted) {
                            return
                        }
                        const latitude = toNumber(e.target.value)
                        updateRows(draft => {
                            const target = draft.find(x => x.rowNumber === row.rowNumber)
                            if (target) {
                                target.latitude = latitude
                            }
                        })
                    }}
                />
            }),
            new DataTableColumnModel({
                fieldName: 'longitude',
                displayName: 'Longitude',
                width: 90,
                customRenderer: (row: HotelImportModel) => <input
                    style={{width:"100%"}}
                    value={typeof row.longitude === 'number' ? row.longitude : ''}
                    onChange={e => {
                        if (row.isLocked || row.isDeleted) {
                            return
                        }
                        const longitude = toNumber(e.target.value)
                        updateRows(draft => {
                            const target = draft.find(x => x.rowNumber === row.rowNumber)
                            if (target) {
                                target.longitude = longitude
                            }
                        })
                    }}
                />
            }),
            new DataTableColumnModel({
                fieldName: 'status',
                displayName: 'Status',
                width: 100,
                customRenderer: (row: HotelImportModel) => {
                    if (row.isDeleted) {
                        return <span style={{ color: '#666' }}><i className='fa fa-trash' /></span>
                    }
                    if (row.isSaved) {
                        return <span style={{ color: '#2e7d32' }}><i className='fa fa-check-circle'/></span>
                    }
                    if (row.isDuplicate) {
                        return <span style={{ color: '#b00020' }}><i className='fa fa-warning' title='Duplicate hotel'/></span>
                    }
                    if (row.hasError) {
                        return <span style={{ color: '#b00020' }}>{row.errors.join(', ')}</span>
                    }
                    return <span style={{ color: '#2e7d32' }}><i className='fa fa-check'/></span>
                }
            }),
            new DataTableColumnModel({
                fieldName: 'remove',
                displayName: '',
                width: 40,
                customRenderer: (row: HotelImportModel) => row.isDuplicate && !row.isDeleted
                    ? <i className='fa fa-trash clickable' onClick={() => onDeleteDuplicate(row)} title='Delete duplicate row' />
                    : undefined
            }),
        ]
        return new DataTableModel({
            name: 'Hotels import',
            fileName: 'hotels-import-preview.xlsx',
            columns,
            customRowClassFunction: (row: HotelImportModel) => row.isDeleted ? 'opacity-50' : undefined,            
            customToolBarComponents: [
                //() => <Link className='a-back-to-list' to={urls.hotels}>{localization.getString('Back to hotels')}<i className='fa fa-plus clickable'/></Link>,
                () => <input
                    style={{width:140}}
                    type='file'
                    accept='.xlsx,.xls'
                    onChange={(e) => setSelectedFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : undefined)}
                />,
                () => <button style={{borderRadius: 5,border:'1px solid #aaa',marginTop:-3,backgroundColor:"white", padding:"5px"}} className='btn btn-primary' onClick={loadPreview} disabled={isPreviewLoading || !selectedFile}>
                    {!selectedFile ? "Select file first" : isPreviewLoading ? 'Loading...' : 'Preview'}
                </button>,
                () =>  ((selectedFile && rows.length>0) ? <button style={{borderRadius:5, border:'1px solid #aaa',marginTop:-3,backgroundColor:(isImportLoading && !canImport) ? "white" : "#33b92d", color:(isImportLoading && !canImport) ? "#aaa" : "white", padding:"5px"}} className='btn btn-primary' onClick={onImport} disabled={isImportLoading || !canImport}>
                    {isImportLoading ? 'Importing...' : 'Import'}
                </button> : undefined),
                () => (lastMessage
                    ? <span style={{fontSize:8}}>{lastMessage}</span>
                    : undefined)
            ]
        })
    }, [rows, countries.countriesOptions, cities.citiesInCountryWithRegionsOptions, selectedFile, isPreviewLoading, isImportLoading, lastMessage, canImport])

    return <React.Fragment>            
        <DataTable
                isFullScreen
                data={rows}
                dataTable={dataTableModel}
        />        
    </React.Fragment>
}

export default HotelsImportPage
